/**
 * Range Slider Custom Element
 * A reusable dual-handle range slider component
 *
 * Emits 'range-slider:change' event on value change (debounced)
 * with detail: { min, max }
 */

class RangeSlider extends HTMLElement {
  constructor() {
    super();

    this._debounceTimer = null;
    this._debounceDelay = 300;

    // Save bound references for proper cleanup
    this._boundMinInputChange = this._onMinInputChange.bind(this);
    this._boundMaxInputChange = this._onMaxInputChange.bind(this);
  }

  connectedCallback() {
    // Get data attributes
    this.rangeMin = parseFloat(this.dataset.min) || 0;
    this.rangeMax = parseFloat(this.dataset.max) || 100;
    this.minValue = parseFloat(this.dataset.minValue) ?? this.rangeMin;
    this.maxValue = parseFloat(this.dataset.maxValue) ?? this.rangeMax;
    this.minGap = parseFloat(this.dataset.gap) || 0;
    this.step = parseFloat(this.dataset.step) || 0;

    // Cache DOM elements
    this.minInput = this.querySelector('input[name="min-input"]');
    this.maxInput = this.querySelector('input[name="max-input"]');
    this.activeTrack = this.querySelector("[data-active-track]");
    this.minHandle = this.querySelector("[data-min-handle]");
    this.maxHandle = this.querySelector("[data-max-handle]");
    this.minDisplay = this.querySelector("[data-min-display]");
    this.maxDisplay = this.querySelector("[data-max-display]");

    // Initialize
    this._updateUI();
    this._bindEvents();
  }

  disconnectedCallback() {
    clearTimeout(this._debounceTimer);
    this.minInput?.removeEventListener("change", this._boundMinInputChange);
    this.maxInput?.removeEventListener("change", this._boundMaxInputChange);
  }

  /* ===========================================
   * GETTERS
   * =========================================== */
  get minPercent() {
    return (
      ((this.minValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100
    );
  }

  get maxPercent() {
    return (
      ((this.maxValue - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100
    );
  }

  /* ===========================================
   * VALUE UPDATES
   * =========================================== */
  _applyStep(val) {
    if (this.step > 0) {
      return Math.round(val / this.step) * this.step;
    }
    return val;
  }

  setMinValue(val) {
    val = this._applyStep(val);
    val = Math.max(this.rangeMin, Math.min(val, this.maxValue - this.minGap));
    this.minValue = val;
    this._updateUI();
  }

  setMaxValue(val) {
    val = this._applyStep(val);
    val = Math.max(this.minValue + this.minGap, Math.min(val, this.rangeMax));
    this.maxValue = val;
    this._updateUI();
  }

  /* ===========================================
   * UI UPDATES
   * =========================================== */
  _updateUI() {
    if (this.minInput) this.minInput.value = this.minValue;
    if (this.maxInput) this.maxInput.value = this.maxValue;

    if (this.activeTrack) {
      this.activeTrack.style.left = `${this.minPercent}%`;
      this.activeTrack.style.right = `${100 - this.maxPercent}%`;
    }

    if (this.minHandle)
      if (this.minPercent > 50) {
        this.minHandle.style.right = `calc(${100 - this.minPercent}% - 0px)`;
        this.minHandle.style.left = "auto";
      } else {
        this.minHandle.style.left = `calc(${this.minPercent}% - 0px)`;
        this.minHandle.style.right = "auto";
      }
    if (this.maxHandle)
      if (this.maxPercent > 50) {
        this.maxHandle.style.right = `calc(${100 - this.maxPercent}% - 0px)`;
        this.maxHandle.style.left = "auto";
      } else {
        this.maxHandle.style.left = `calc(${this.maxPercent}% - 0px)`;
        this.maxHandle.style.right = "auto";
      }

    if (this.minDisplay) this.minDisplay.textContent = this.minValue;
    if (this.maxDisplay) this.maxDisplay.textContent = this.maxValue;
  }

  /* ===========================================
   * EVENT EMISSION
   * =========================================== */
  _emitChange() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent("range-slider:change", {
          bubbles: true,
          detail: {
            min: this.minValue,
            max: this.maxValue,
          },
        }),
      );
    }, this._debounceDelay);
  }

  /* ===========================================
   * EVENT BINDING
   * =========================================== */

  _onMinInputChange(e) {
    const val = parseFloat(e.target.value);
    if (!isNaN(val)) {
      this.setMinValue(val);
      this._emitChange();
    }
  }

  _onMaxInputChange(e) {
    const val = parseFloat(e.target.value);
    if (!isNaN(val)) {
      this.setMaxValue(val);
      this._emitChange();
    }
  }

  _bindEvents() {
    this.minInput?.addEventListener("change", this._boundMinInputChange);
    this.maxInput?.addEventListener("change", this._boundMaxInputChange);

    this._bindHandleDrag(this.minHandle, "min");
    this._bindHandleDrag(this.maxHandle, "max");
  }

  _bindHandleDrag(handle, type) {
    if (!handle) return;
    const slider = handle.parentElement;

    // Mouse events
    handle.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const rect = slider.getBoundingClientRect();
      const startPercent = type === "min" ? this.minPercent : this.maxPercent;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        const dpercent = (dx / rect.width) * 100;
        const newPercent = this._clampPercent(startPercent + dpercent, type);
        const newValue =
          this.rangeMin + (newPercent / 100) * (this.rangeMax - this.rangeMin);

        if (type === "min") {
          this.minValue = this._applyStep(newValue);
        } else {
          this.maxValue = this._applyStep(newValue);
        }
        this._updateUI();
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        this._emitChange();
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    });

    // Touch events
    handle.addEventListener("touchstart", (e) => {
      e.preventDefault();
      const startX = e.touches[0].clientX;
      const rect = slider.getBoundingClientRect();
      const startPercent = type === "min" ? this.minPercent : this.maxPercent;

      const onMove = (ev) => {
        const dx = ev.touches[0].clientX - startX;
        const dpercent = (dx / rect.width) * 100;
        const newPercent = this._clampPercent(startPercent + dpercent, type);
        const newValue =
          this.rangeMin + (newPercent / 100) * (this.rangeMax - this.rangeMin);

        if (type === "min") {
          this.minValue = this._applyStep(newValue);
        } else {
          this.maxValue = this._applyStep(newValue);
        }
        this._updateUI();
      };

      const onEnd = () => {
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
        this._emitChange();
      };

      document.addEventListener("touchmove", onMove);
      document.addEventListener("touchend", onEnd);
    });
  }

  _clampPercent(percent, type) {
    const gapPercent = (this.minGap / (this.rangeMax - this.rangeMin)) * 100;

    if (type === "min") {
      return Math.max(0, Math.min(percent, this.maxPercent - gapPercent));
    } else {
      return Math.max(this.minPercent + gapPercent, Math.min(100, percent));
    }
  }
}

customElements.define("range-slider", RangeSlider);
